import { PagingControls } from '../../../components/ui/PagingControls/PagingControls';
import { LoadingContainer } from '../../../components/ui/LoadingContainer/LoadingContainer';
import { SortableHeader } from '../../../components/ui/SortableHeader/SortableHeader';
import Container from '../../ui/Container/Container';
import styles from './MembershipTypeLookup.module.css';
import useMembershipTypeLookup from './useMembershipTypeLookup';
import { IMembershipTypeDto } from '../../../utils/constants/interfaces';

interface MembershipTypeLookupProps {
  membershipTypeId?: string;
  visible?: boolean;
  modalView?: boolean;
  requireKeywords?: boolean;
  onChange: (membershipTypeId: string) => void;
}

export const MembershipTypeLookup = ({
  visible = true,
  modalView = false,
  requireKeywords = false,
  onChange
}: MembershipTypeLookupProps) => {
  const manager = useMembershipTypeLookup(requireKeywords, onChange);

  if (!visible) return <></>;

  return (
    <div className={styles.mainContainer}>
      {/* Filters Section - Now Above the Table */}
      <Container className={styles.filtersSection}>
        <div className="row g-3">
          <div className="col-md-6">
            <div className="form-group">
              <label htmlFor="keywordSearch">Membership Type Search</label>
              <input
                id="keywordSearch"
                type="text"
                className="form-control"
                placeholder="Search by name, category, or class"
                onChange={(e) => {
                  e.preventDefault();
                  manager.debouncedMembershipTypeSearch(e.target.value);
                }}
              />
            </div>
          </div>
        </div>
      </Container>

      {/* Results Section - Now Below the Filters */}
      <Container>
        <LoadingContainer
          isLoading={manager.loadingSearch && manager.membershipTypePagedResults.resultCount === 0}>
          <Container
            visible={
              manager.hasSearchParameters && manager.membershipTypePagedResults.resultCount === 0
            }>
            <div>No membership types found.</div>
          </Container>
          <Container visible={manager.membershipTypePagedResults.resultCount > 0}>
            <>
              <div
                className={
                  modalView ? styles.lookupSearchResultsModal : styles.lookupSearchResults
                }>
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <SortableHeader
                        text="Name"
                        sortField="name"
                        active={manager.searchParameters.sortField === 'name'}
                        sortDirection={manager.searchParameters.sortDirection}
                        onClick={manager.changeSort}
                      />
                      <SortableHeader
                        text="Category"
                        sortField="categoryCode"
                        active={manager.searchParameters.sortField === 'categoryCode'}
                        sortDirection={manager.searchParameters.sortDirection}
                        onClick={manager.changeSort}
                      />
                      <SortableHeader
                        text="Class"
                        sortField="classCode"
                        active={manager.searchParameters.sortField === 'classCode'}
                        sortDirection={manager.searchParameters.sortDirection}
                        onClick={manager.changeSort}
                      />
                      <SortableHeader
                        text="Initiation Fee"
                        sortField="initiationFee"
                        active={manager.searchParameters.sortField === 'initiationFee'}
                        sortDirection={manager.searchParameters.sortDirection}
                        onClick={manager.changeSort}
                      />
                      <SortableHeader
                        text="Dues"
                        sortField="dues"
                        active={manager.searchParameters.sortField === 'dues'}
                        sortDirection={manager.searchParameters.sortDirection}
                        onClick={manager.changeSort}
                      />
                      <SortableHeader
                        text="Sort Order"
                        sortField="sortOrder"
                        active={manager.searchParameters.sortField === 'sortOrder'}
                        sortDirection={manager.searchParameters.sortDirection}
                        onClick={manager.changeSort}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {manager.membershipTypePagedResults.results?.map((type: IMembershipTypeDto) => (
                      <tr
                        key={type.id}
                        onClick={() => manager.selectMembershipType(type.id.toString())}
                        style={{ cursor: 'pointer' }}>
                        <td>{type.name}</td>
                        <td>{type.categoryCode?.codeValue ?? ''}</td>
                        <td>{type.classCode?.codeValue ?? ''}</td>
                        <td>${type.initiationFee.toFixed(2)}</td>
                        <td>${type.dues.toFixed(2)}</td>
                        <td>{type.sortOrder}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="pt-4">
                <PagingControls
                  page={manager.membershipTypePagedResults.currentPage ?? 1}
                  pageCount={manager.membershipTypePagedResults.pageCount ?? 1}
                  maxPlusOrMinusPagesToShow={3}
                  onPageChange={manager.changePage}
                />
              </div>
            </>
          </Container>
        </LoadingContainer>
      </Container>
    </div>
  );
};
